import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { getMaintenanceState } from '../api/adminOperations';

export const MAINTENANCE_MODES = {
  OFF: 'off',
  FULL_STOP: 'full_stop',
  READ_ONLY: 'read_only',
};

const MaintenanceContext = createContext({
  maintenance: null,
  maintenanceLoading: true,
  maintenanceError: null,
  refreshMaintenance: () => {},
  isMaintenanceActive: false,
  isRoleAllowed: () => true,
});

export function MaintenanceProvider({ children }) {
  const [maintenance, setMaintenance] = useState(null);
  const [maintenanceLoading, setMaintenanceLoading] = useState(true);
  const [maintenanceError, setMaintenanceError] = useState(null);

  const fetchMaintenance = useCallback(async ({ suppressLoading = false } = {}) => {
    if (!suppressLoading) {
      setMaintenanceLoading(true);
    }
    setMaintenanceError(null);
    let lastResponseStatus = null;
    try {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[maintenance] fetch start');
      }
      const result = await getMaintenanceState();
      lastResponseStatus = result.status;

      // 304 Not Modified は現状態を維持して終了
      if (result.status === 304) {
        setMaintenanceLoading(false);
        return;
      }

      const data = result.data || {};

      // デバッグ出力（開発時のみ）
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[maintenance] fetch', {
          status: result.status,
          ok: result.ok,
          success: data?.success,
          maintenance: data?.maintenance,
        });
      }

      if (result.ok && data.success !== false) {
        setMaintenance(data.maintenance || null);
        return;
      }

      // 非200の場合は既存状態を維持しつつエラーのみ通知
      throw new Error(data?.message || 'メンテナンス情報の取得に失敗しました');
    } catch (err) {
      // 503/5xx の場合は最低限の情報を維持（読取専用をデフォルトフォールバック）
      if (lastResponseStatus && lastResponseStatus >= 500) {
        setMaintenance((prev) =>
          prev || {
            maintenanceMode: MAINTENANCE_MODES.READ_ONLY,
            readOnly: true,
            message: 'メンテナンス中です。情報を見ることはできますが更新はできません。',
            mobileMode: 'inherit',
          }
        );
      }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[maintenance] fetch error', err?.message || err);
      }
      setMaintenanceError(err.message);
    } finally {
      if (!suppressLoading) {
        setMaintenanceLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchMaintenance();
    const id = setInterval(() => {
      fetchMaintenance({ suppressLoading: true });
    }, 60 * 1000);
    return () => clearInterval(id);
  }, [fetchMaintenance]);

  // 通信復帰（offline→online）時に一度だけ状態を軽く再取得する。エッジ検出は
  // ConnectivityContext に集約済みなので、ここではイベント購読のみ。
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onRestored = () => fetchMaintenance({ suppressLoading: true });
    window.addEventListener('casticart:connectivity-restored', onRestored);
    return () => window.removeEventListener('casticart:connectivity-restored', onRestored);
  }, [fetchMaintenance]);

  useEffect(() => {
    if (maintenance?.normalizedFromUnknown && process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn('[maintenance] Unknown maintenance mode was normalized to full_stop. Please re-save maintenance settings.');
    }
  }, [maintenance?.normalizedFromUnknown]);

  const maintenanceMode = maintenance?.maintenanceMode || MAINTENANCE_MODES.OFF;
  const isMaintenanceActive = maintenanceMode !== MAINTENANCE_MODES.OFF;
  const isReadOnlyMode =
    maintenanceMode === MAINTENANCE_MODES.READ_ONLY || (maintenance && maintenance.readOnly);

  // グローバルにも現在のメンテナンス状態をエクスポートしておく（fetch ラッパ等から参照）
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.__CASTICART_MAINTENANCE_STATE = {
      maintenance,
      maintenanceMode,
      isMaintenanceActive,
      isReadOnly: isReadOnlyMode,
    };
  }, [maintenance, maintenanceMode, isMaintenanceActive, isReadOnlyMode]);

  const isRoleAllowed = useCallback(
    (role) => {
      if (!isMaintenanceActive) return true;
      if (!role) return false;
      switch (maintenanceMode) {
        case MAINTENANCE_MODES.FULL_STOP:
          return false;
        case MAINTENANCE_MODES.READ_ONLY:
          return role === 'admin' || role === 'artist';
        case MAINTENANCE_MODES.ADMIN_ONLY:
          return role === 'admin';
        case MAINTENANCE_MODES.ARTIST_ONLY:
          return role === 'artist';
        default:
          return false;
      }
    },
    [isMaintenanceActive, maintenanceMode]
  );

  const refreshMaintenance = useCallback(() => fetchMaintenance({ suppressLoading: false }), [fetchMaintenance]);

  const value = useMemo(
    () => ({
      maintenance,
      maintenanceLoading,
      maintenanceError,
      refreshMaintenance,
      maintenanceMode,
      isMaintenanceActive,
      isReadOnly: isReadOnlyMode,
      isRoleAllowed,
    }),
    [
      refreshMaintenance,
      isMaintenanceActive,
      isReadOnlyMode,
      isRoleAllowed,
      maintenanceMode,
      maintenance,
      maintenanceError,
      maintenanceLoading,
    ]
  );

  return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
}

export function useMaintenance() {
  return useContext(MaintenanceContext);
}
